import axios from 'axios';
import { readonly, ref } from 'vue';
import { timeout } from '@/services/timeout';

const PING_FREQUENCY_LIMIT = 10000; // 10 seconds
const FAILURE_QUANTITY = 5;
const ACTIVE_EVENTS = ['pointerdown', 'pointermove', 'keydown'];
const MESSAGE_RETRY_DELAY = 2000; // 2 seconds
const MAX_RETRIES = 3;

const state = ref({
    isScorm: false,
    token: null,
    socket: null,
    isConnected: false,
    isConnecting: false,
    failures: 0,
    queue: new Map(),
    lastActive: Date.now(),
    refreshing: false,
    offline: false
});

const generateMessageId = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const processQueue = async (course) => {
    for (const [messageId, message] of state.value.queue.entries()) {
        if (message.retries >= MAX_RETRIES) {
            state.value.queue.delete(messageId);
            continue;
        }

        try {
            await sendMessage(course, message);
        } catch (_error) {
            message.retries = (message.retries || 0) + 1;
            state.value.queue.set(messageId, message);
        }
    }
};

const connect = async (apiUrl, course) => {
    if (state.value.offline) return;
    if (!course || !state.value.token || state.value.isConnecting) return;
    if (state.value.isConnected) return;

    state.value.isConnecting = true;
    if (state.value.socket) state.value.socket.close();

    try {
        const socket = new WebSocket(`${apiUrl.replace('http', 'ws')}/tracking/ws`);
        state.value.socket = socket;

        socket.addEventListener('open', () => {
            state.value.isConnected = true;
            state.value.isConnecting = false;
            timeout(() => processQueue(course), MESSAGE_RETRY_DELAY);
            ACTIVE_EVENTS.forEach(name => document.addEventListener(name, ping));
        });

        socket.addEventListener('error', () => {
            if (socket.readyState === WebSocket.OPEN) return;
            socket.close();
        });

        socket.addEventListener('message', async (message) => {
            const response = JSON.parse(message.data);
            if (response.error) {
                await refresh(course);
                return timeout(() => processQueue(course), MESSAGE_RETRY_DELAY);
            }
            if (response.type === 'pong') return;
            
            if (response.messageId && state.value.queue.has(response.messageId)) {
                state.value.queue.delete(response.messageId);
            }
            if (response.refresh_token) await refresh(course);
            
            postSCORMData(response);
        });

        socket.addEventListener('close', () => {
            state.value.isConnected = false;
            state.value.isConnecting = false;
            ACTIVE_EVENTS.forEach(name => document.removeEventListener(name, ping));

            state.value.failures += 1;
            if (state.value.failures < FAILURE_QUANTITY) {
                timeout(() => connect(apiUrl, course), 2000);
            }
        });
    } catch (_error) {
        state.value.isConnecting = false;
    }
};

const ping = () => {
    const now = Date.now();
    if ((now - state.value.lastActive) < PING_FREQUENCY_LIMIT) return;
    send(null, 'ping');
};

const sendMessage = async (course, message) => {
    if (state.value.offline) return;
    message.token = state.value.token;
    if (state.value.socket?.readyState === WebSocket.OPEN && state.value.isConnected) {
        state.value.socket.send(JSON.stringify(message));
        state.value.lastActive = Date.now();
        return;
    }

    if (message.type === 'ping') return;
    if (state.value.isConnecting) return;
    sendHttpMessage(message);
};

const sendHttpMessage = async (message) => {
    if (state.value.offline) return;
    try {
        const response = await axios.post('/api/tracking/http', message, {
            headers: { Authorization: `Bearer ${state.value.token}` },
            withCredentials: false
        });
        
        state.value.lastActive = Date.now();
        
        if (response.data.messageId && state.value.queue.has(response.data.messageId)) {
            state.value.queue.delete(response.data.messageId);
        }
        if (response.data.refresh_token) await refresh(course);
        postSCORMData(response.data);
        
        return response;
    } catch (e) {
        throw e;
    }
};

const send = async (course, type, screenId = null, correct = null, context = null, elapsedTime = 0) => {
    if (state.value.offline) return;
    if (!course && type !== 'ping') return;
    if (Array.isArray(context)) context = null;

    const messageId = generateMessageId();
    const message = type === 'ping' ? { type } : {
        messageId,
        type,
        screenId: screenId ? Number(screenId) : null,
        correct,
        context,
        elapsedTime
    };

    if (type !== 'ping') state.value.queue.set(messageId, { ...message, retries: 0 });

    try {
        await sendMessage(course, message);
    } catch (error) {
        if (error.response.error === 'Invalid token') {
            return refresh(course);
        }
        timeout(() => processQueue(course), MESSAGE_RETRY_DELAY);
    }
};

const postSCORMData = (data) => {
    if (window.self !== window.top) window.parent.postMessage(JSON.stringify(data), '*');
};

const refresh = async (course) => {
    if (state.value.offline) return;
    if (state.value.refreshing || !state.value.token) return;
    
    state.value.refreshing = true;
    try {
        const response = await axios.post('/api/learn/refresh', {
            token: state.value.token
        });
        
        if (response.data.token) {
            state.value.token = response.data.token;
        }
    } catch (_error) {
        window.location.reload();
    } finally {
        state.value.refreshing = false;
        timeout(() => processQueue(course), MESSAGE_RETRY_DELAY);
    }
};

// Action methods
const start = (course, screenId) => send(course, 'start', screenId);
const progress = (course, screenId, context = {}) => send(course, 'progress', screenId, null, context);
const end = (course, screenId, elapsedTime = 0) => send(course, 'end', screenId, null, null, elapsedTime);
const complete = (course, screenId, correct = false, context = null) => send(course, 'complete', screenId, correct, context);
const answer = (course, screenId, correct = false, context = null) => send(course, 'answer', screenId, correct, context);
const retry = (course, screenId) => send(course, 'retry', screenId);

const setToken = (token) => state.value.token = token;
const setOffline = () => state.value.offline = true;

export default readonly({
    connect,
    start,
    progress,
    end,
    complete,
    answer,
    retry,
    setToken,
    refresh,
    setOffline,
    state
});
